import { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useTenant, useUser } from '../stores/authStore';
import { useNavigationStore } from '../stores/navigationStore';
import LanguageSwitcher from './LanguageSwitcher';
import { Reorder } from 'framer-motion';
import {
  LayoutDashboard,
  Building2,
  Calendar,
  TrendingUp,
  Key,
  Webhook,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Beaker,
  Wine,
  Sparkles,
  ClipboardList,
  BarChart3,
  Activity,
  UtensilsCrossed,
  Star,
  GripVertical,
  SlidersHorizontal,
  RotateCcw,
  LayoutGrid,
  HelpCircle,
  Receipt,
  Users,
  Megaphone,
} from 'lucide-react';
import type { UserRole } from '../types';
import clsx from 'clsx';

interface NavChild {
  nameKey: string;
  href: string;
  roles?: UserRole[];
}

interface NavItem {
  nameKey: string;
  href: string;
  icon: React.ElementType;
  children?: NavChild[];
  roles?: UserRole[];
}

// Helper function to check if user has admin privileges
const isAdminUser = (role?: UserRole): boolean => {
  return role === 'owner' || role === 'admin';
};

const navigationItems: NavItem[] = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav.distillery', href: '/distillery', icon: Building2 },
  {
    nameKey: 'insights.brewmaster',
    href: '/brewmaster',
    icon: Beaker,
    children: [
      { nameKey: 'nav.recipes', href: '/brewmaster/recipes' },
      { nameKey: 'nav.batches', href: '/brewmaster/batches' },
      { nameKey: 'nav.insights', href: '/brewmaster/insights' },
    ],
  },
  {
    nameKey: 'nav.gastronomy',
    href: '/gastronomy',
    icon: UtensilsCrossed,
    children: [
      { nameKey: 'nav.venues', href: '/venues' },
      { nameKey: 'nav.liveOrders', href: '/live-orders' },
      { nameKey: 'nav.analytics', href: '/analytics' },
      { nameKey: 'nav.pourCost', href: '/pour-cost' },
      { nameKey: 'nav.happyHour', href: '/happy-hour' },
      { nameKey: 'nav.events', href: '/events' },
    ],
  },
  { nameKey: 'nav.trends', href: '/trends', icon: TrendingUp },
  { nameKey: 'nav.releaseNotes', href: '/release-notes', icon: Megaphone },
  {
    nameKey: 'nav.settings',
    href: '/settings',
    icon: Settings,
    children: [
      { nameKey: 'nav.settingsGeneral', href: '/settings' },
      { nameKey: 'nav.team', href: '/team', roles: ['owner', 'admin'] },
      { nameKey: 'nav.apiKeys', href: '/api-keys' },
      { nameKey: 'nav.webhooks', href: '/webhooks' },
      { nameKey: 'nav.monitoring', href: '/monitoring', roles: ['owner', 'admin'] },
    ],
  },
];

// Icon map for dynamic (custom) favorites
const customIconMap: Record<string, React.ElementType> = {
  Building2,
  LayoutDashboard,
  Calendar,
  UtensilsCrossed,
  Wine,
  Star,
  LayoutGrid,
  Receipt,
  Users,
};

function resolveFavorite(
  href: string,
  customFavorites: Record<string, { label: string; iconName: string }>
): { nameKey: string; icon: React.ElementType; href: string; isCustom?: boolean } | null {
  // 1. Static navigation lookup
  for (const item of navigationItems) {
    if (item.href === href && !item.children) return { nameKey: item.nameKey, icon: item.icon, href };
    if (item.children) {
      const child = item.children.find(c => c.href === href);
      if (child) return { nameKey: child.nameKey, icon: item.icon, href };
    }
  }
  // 2. Custom favorites fallback
  const custom = customFavorites[href];
  if (custom) {
    const icon = customIconMap[custom.iconName] || Building2;
    return { nameKey: custom.label, icon, href, isCustom: true };
  }
  return null;
}

export function Layout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['nav.gastronomy']);
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const user = useUser();
  const tenant = useTenant();

  const { favorites, customFavorites, navOrder, editMode, toggleFavorite, setNavOrder, setEditMode, resetToDefaults } =
    useNavigationStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const isActive = (href: string) => location.pathname.startsWith(href);

  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => location.pathname.startsWith(child.href));
    }
    return isActive(item.href);
  };

  // Sort navigation items by stored order
  const sortedNavItems = useMemo(() => {
    const filtered = navigationItems.filter(
      (item) => !item.roles || (user?.role && item.roles.includes(user.role))
    );
    return [...filtered].sort((a, b) => {
      const idxA = navOrder.indexOf(a.nameKey);
      const idxB = navOrder.indexOf(b.nameKey);
      // Items not in navOrder go to the end
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [navOrder, user?.role]);

  // Resolve favorites to renderable items
  const resolvedFavorites = useMemo(() => {
    return favorites
      .map((href) => resolveFavorite(href, customFavorites))
      .filter((f): f is NonNullable<ReturnType<typeof resolveFavorite>> => f !== null);
  }, [favorites, customFavorites]);

  // Collect all favoritable hrefs (top-level without children + all children)
  const isFavorited = (href: string) => favorites.includes(href);

  // Auto-expand parent menus when navigating to child pages
  useEffect(() => {
    navigationItems.forEach((item) => {
      if (item.children && isParentActive(item) && !expandedItems.includes(item.nameKey)) {
        setExpandedItems((prev) => [...prev, item.nameKey]);
      }
    });
  }, [location.pathname]);

  // nameKeys for Reorder (framer-motion needs a value array)
  const reorderValues = sortedNavItems.map((item) => item.nameKey);

  const handleReorder = (newOrder: string[]) => {
    setNavOrder(newOrder);
  };

  return (
    <div className="min-h-screen bg-dark-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-dark-950/80 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-72 bg-dark-900 border-r border-dark-800 transform transition-transform duration-200 lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-dark-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
              <Wine className="w-5 h-5 text-dark-900" />
            </div>
            <span className="font-display font-bold text-lg text-dark-50">
              SpiritVault<span className="text-brand-500">Pro</span>
            </span>
          </Link>
          <button
            className="lg:hidden text-dark-400 hover:text-dark-100"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tenant Info */}
        <div className="px-4 py-4 border-b border-dark-800">
          <div className="text-sm text-dark-400">Organization</div>
          <div className="font-medium text-dark-100 truncate">{tenant?.name}</div>
          <div className="text-xs text-brand-500 capitalize">{tenant?.tier} Plan</div>
        </div>

        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto">
          {/* Favorites Section */}
          {resolvedFavorites.length > 0 && (
            <div className="px-3 pt-4 pb-2">
              <div className="flex items-center gap-2 px-3 mb-2">
                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                <span className="text-xs font-medium text-dark-500 uppercase tracking-wider">
                  Favoriten
                </span>
              </div>
              <div className="space-y-0.5">
                {resolvedFavorites.map((fav) => (
                  <Link
                    key={fav.href}
                    to={fav.href}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm',
                      isActive(fav.href)
                        ? 'text-brand-400 bg-brand-500/10'
                        : 'text-dark-400 hover:text-dark-100 hover:bg-dark-800'
                    )}
                  >
                    <fav.icon className="w-4 h-4" />
                    <span>{fav.isCustom ? fav.nameKey : t(fav.nameKey)}</span>
                  </Link>
                ))}
              </div>
              <div className="mx-3 mt-3 border-b border-dark-800" />
            </div>
          )}

          {/* Navigation Header + Edit Toggle */}
          <div className="flex items-center justify-between px-6 pt-3 pb-1">
            <span className="text-xs font-medium text-dark-500 uppercase tracking-wider">
              Navigation
            </span>
            <button
              onClick={() => setEditMode(!editMode)}
              className={clsx(
                'p-1 rounded transition-colors',
                editMode
                  ? 'text-brand-400 bg-brand-500/10'
                  : 'text-dark-500 hover:text-dark-300'
              )}
              title={editMode ? 'Bearbeitung beenden' : 'Navigation anpassen'}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="px-3 pb-4 space-y-1">
            {editMode ? (
              <Reorder.Group
                axis="y"
                values={reorderValues}
                onReorder={handleReorder}
                className="space-y-1"
              >
                {sortedNavItems.map((item) => (
                  <Reorder.Item
                    key={item.nameKey}
                    value={item.nameKey}
                    className="list-none"
                  >
                    <NavItemRenderer
                      item={item}
                      t={t}
                      isActive={isActive}
                      isParentActive={isParentActive}
                      expandedItems={expandedItems}
                      toggleExpanded={toggleExpanded}
                      editMode={true}
                      isFavorited={isFavorited}
                      toggleFavorite={toggleFavorite}
                      userRole={user?.role}
                    />
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            ) : (
              sortedNavItems.map((item) => (
                <NavItemRenderer
                  key={item.nameKey}
                  item={item}
                  t={t}
                  isActive={isActive}
                  isParentActive={isParentActive}
                  expandedItems={expandedItems}
                  toggleExpanded={toggleExpanded}
                  editMode={false}
                  isFavorited={isFavorited}
                  toggleFavorite={toggleFavorite}
                  userRole={user?.role}
                />
              ))
            )}

            {/* Reset link in edit mode */}
            {editMode && (
              <button
                onClick={resetToDefaults}
                className="flex items-center gap-2 px-4 py-2 mt-2 text-xs text-dark-500 hover:text-dark-300 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Zurücksetzen</span>
              </button>
            )}
          </nav>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-dark-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-brand-500/20 rounded-full flex items-center justify-center">
              <span className="text-brand-400 font-medium">
                {user?.first_name?.[0] || user?.email[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-dark-100 truncate">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-xs text-dark-500 truncate">{user?.email}</div>
            </div>
          </div>
          <a
            href="/docs/SpiritVault-Benutzerhandbuch.html"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-dark-400 hover:text-dark-100 hover:bg-dark-800 rounded-lg transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            <span>{t('nav.help')}</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-dark-400 hover:text-dark-100 hover:bg-dark-800 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-dark-900/80 backdrop-blur-sm border-b border-dark-800 lg:px-8">
          <button
            className="lg:hidden text-dark-400 hover:text-dark-100 mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1" />
          {/* Language Switcher */}
          <LanguageSwitcher />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* ─── Nav Item Renderer ─── */

interface NavItemRendererProps {
  item: NavItem;
  t: (key: string) => string;
  isActive: (href: string) => boolean;
  isParentActive: (item: NavItem) => boolean;
  expandedItems: string[];
  toggleExpanded: (name: string) => void;
  editMode: boolean;
  isFavorited: (href: string) => boolean;
  toggleFavorite: (href: string) => void;
  userRole?: UserRole;
}

function NavItemRenderer({
  item,
  t,
  isActive,
  isParentActive,
  expandedItems,
  toggleExpanded,
  editMode,
  isFavorited,
  toggleFavorite,
  userRole,
}: NavItemRendererProps) {
  return (
    <div>
      {item.children ? (
        <>
          <div className="flex items-center">
            {editMode && (
              <GripVertical className="w-4 h-4 text-dark-600 cursor-grab mr-1 flex-shrink-0" />
            )}
            <button
              onClick={() => toggleExpanded(item.nameKey)}
              className={clsx(
                'flex-1 flex items-center justify-between px-4 py-3 text-dark-400 hover:text-dark-100 hover:bg-dark-800 rounded-lg transition-colors',
                isParentActive(item) && 'text-brand-400 bg-brand-500/10'
              )}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                <span>{t(item.nameKey)}</span>
              </div>
              <ChevronDown
                className={clsx(
                  'w-4 h-4 transition-transform',
                  expandedItems.includes(item.nameKey) && 'rotate-180'
                )}
              />
            </button>
          </div>
          {expandedItems.includes(item.nameKey) && (
            <div className={clsx('mt-1 space-y-1', editMode ? 'ml-9' : 'ml-8')}>
              {item.children
                .filter((child) => !child.roles || (userRole && child.roles.includes(userRole)))
                .map((child) => (
                <div key={child.href} className="flex items-center group">
                  <Link
                    to={child.href}
                    className={clsx(
                      'flex-1 block px-4 py-2 text-sm rounded-lg transition-colors',
                      isActive(child.href)
                        ? 'text-brand-400 bg-brand-500/10'
                        : 'text-dark-400 hover:text-dark-100 hover:bg-dark-800'
                    )}
                  >
                    {t(child.nameKey)}
                  </Link>
                  {editMode && (
                    <button
                      onClick={(e) => { e.preventDefault(); toggleFavorite(child.href); }}
                      className="p-1 flex-shrink-0 transition-colors"
                      title={isFavorited(child.href) ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                    >
                      <Star
                        className={clsx(
                          'w-3.5 h-3.5 transition-colors',
                          isFavorited(child.href)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-dark-600 hover:text-amber-400'
                        )}
                      />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center">
          {editMode && (
            <GripVertical className="w-4 h-4 text-dark-600 cursor-grab mr-1 flex-shrink-0" />
          )}
          <Link
            to={item.href}
            className={clsx(
              'flex-1 flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
              isActive(item.href)
                ? 'text-brand-400 bg-brand-500/10 border-r-2 border-brand-500'
                : 'text-dark-400 hover:text-dark-100 hover:bg-dark-800'
            )}
          >
            <item.icon className="w-5 h-5" />
            <span>{t(item.nameKey)}</span>
          </Link>
          {editMode && (
            <button
              onClick={(e) => { e.preventDefault(); toggleFavorite(item.href); }}
              className="p-1 flex-shrink-0 transition-colors"
              title={isFavorited(item.href) ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
            >
              <Star
                className={clsx(
                  'w-3.5 h-3.5 transition-colors',
                  isFavorited(item.href)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-dark-600 hover:text-amber-400'
                )}
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
